import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarClock, MessageSquare, PhoneCall, ExternalLink, X, RefreshCw, Plus, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatBill } from "@/lib/format";
import { toast } from "sonner";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";

const inr = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const OVERDUE_DAYS = 15;

function daysOverdue(dateStr) {
  if (!dateStr) return 0;
  const then = new Date(dateStr + "T00:00:00").getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24)));
}

/**
 * Combined Dashboard dialog that shows BOTH:
 *  a) Follow-ups whose promised date is 15+ days overdue (broken promises)
 *  b) Pending receipts (balance > 0) with no follow-up yet, that are 15+ days old
 * Staff can record a new promised date + note directly from this list — the
 * entry then moves into the Follow-Up module and stays here until paid.
 */
export default function OverdueFollowUpsDialog({ open, onOpenChange, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addNoteRow, setAddNoteRow] = useState(null);
  const [waRow, setWaRow] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // A cutoff date = today - 15 days
      const cutoff = (() => {
        const d = new Date(); d.setDate(d.getDate() - OVERDUE_DAYS);
        return d.toISOString().slice(0, 10);
      })();

      const [fuRes, prRes] = await Promise.allSettled([
        // (a) Existing follow-ups with promised date ≥ 15 days in the past
        api.get("/follow-ups", { params: { status: "pending", date_to: cutoff } }),
        // (b) Overdue receipts with no follow-up entry — treat pending-reminder data as source
        api.get("/dashboard/pending-reminders", { params: { min_days: OVERDUE_DAYS } }),
      ]);

      const fuList = fuRes.status === "fulfilled" ? (fuRes.value.data || []) : [];
      const prList = prRes.status === "fulfilled" ? (prRes.value.data?.items || []) : [];

      // Build a set of receipt_ids that already have a follow-up (so we don't double-list them)
      const withFollowUp = new Set(fuList.map((f) => f.receipt_id));

      const followUpRows = fuList.map((f) => ({
        source: "follow_up",
        id: f.id,
        receipt_id: f.receipt_id,
        customer_name: f.customer_name,
        mobile: f.mobile,
        doc_no: f.doc_no,
        service_name: f.service_name,
        balance: f.balance,
        promised: f.follow_up_date,
        days_overdue: daysOverdue(f.follow_up_date),
        last_note: f.last_note,
      }));

      const reminderRows = prList
        .filter((r) => !withFollowUp.has(r.id))
        .map((r) => ({
          source: "pending_receipt",
          id: `pr-${r.id}`,
          receipt_id: r.id,
          customer_name: r.customer_name,
          mobile: r.mobile,
          doc_no: r.application_id || r.doc_no,
          service_name: r.service_name,
          balance: r.balance,
          promised: null,
          days_overdue: r.days_overdue,
          last_note: null,
        }));

      const combined = [...followUpRows, ...reminderRows].sort((a, b) => b.days_overdue - a.days_overdue);
      setItems(combined);
      if (typeof onCountChange === "function") onCountChange(combined.length);
    } catch {
      setItems([]);
      if (typeof onCountChange === "function") onCountChange(0);
    } finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const openWhatsApp = (r) => {
    if (!r.mobile) {
      toast.error("No mobile number saved for this customer");
      return;
    }
    setWaRow(r);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white max-w-6xl max-h-[85vh] overflow-hidden flex flex-col p-0"
        data-testid="overdue-followups-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-red-200 bg-gradient-to-r from-red-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-red-900">
            <CalendarClock className="h-5 w-5 text-red-600" />
            Overdue Follow-Ups — 15+ Days
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {items.length > 0
              ? `${items.length} outstanding balance${items.length > 1 ? "s" : ""}. Click ` + `"+ New Date"` + ` to record the new payment date the customer promises.`
              : loading
              ? "Loading…"
              : "All caught up — no balances overdue by 15+ days."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          {items.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <div className="text-sm font-semibold text-slate-600">All caught up!</div>
              <div className="text-xs">No overdue balances 15+ days old.</div>
            </div>
          ) : (
            <table className="w-full text-sm crm-table" data-testid="overdue-followups-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold">Mobile</th>
                  <th className="px-3 py-2 text-left font-semibold">Doc No</th>
                  <th className="px-3 py-2 text-left font-semibold">Service</th>
                  <th className="px-3 py-2 text-right font-semibold">Balance</th>
                  <th className="px-3 py-2 text-left font-semibold">Promised</th>
                  <th className="px-3 py-2 text-right font-semibold">Days Late</th>
                  <th className="px-3 py-2 text-left font-semibold">Last Note</th>
                  <th className="px-3 py-2 text-right font-semibold w-64">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} data-testid={`overdue-fu-row-${r.receipt_id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-700">{r.mobile || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-600">{formatBill(r.doc_no)}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{r.service_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num font-bold text-red-600">{inr(r.balance)}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{r.promised || <span className="italic text-slate-400">(not set)</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200">
                        {r.days_overdue}d
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs max-w-xs truncate" title={r.last_note}>{r.last_note || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 justify-end">
                        <Button
                          size="sm"
                          onClick={() => openWhatsApp(r)}
                          className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                          data-testid={`overdue-wa-${r.receipt_id}`}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" /> WA
                        </Button>
                        <a href={`tel:${r.mobile}`} className="p-1 rounded hover:bg-slate-100" title="Call"><PhoneCall className="h-3.5 w-3.5 text-emerald-700" /></a>
                        <Button
                          size="sm"
                          onClick={() => setAddNoteRow(r)}
                          className="h-7 px-2 bg-[color:var(--brand-navy)] hover:bg-slate-800 text-white text-[11px]"
                          data-testid={`overdue-newdate-${r.receipt_id}`}
                        >
                          <Plus className="h-3 w-3 mr-1" /> New Date
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Recording a new promised date moves the entry into <span className="font-semibold">Follow-Up</span>. Once the receipt is paid, it auto-clears from this list.
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={load}
              disabled={loading}
              variant="outline"
              className="h-8 text-xs font-semibold"
              data-testid="overdue-followups-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              onClick={() => { onOpenChange(false); nav("/app/follow-up"); }}
              className="h-8 bg-[color:var(--brand-navy)] hover:bg-slate-800 text-white text-xs font-semibold"
              data-testid="overdue-followups-open-page"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open Follow-Up Page
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold"
              data-testid="overdue-followups-close"
            >
              <X className="h-3.5 w-3.5 mr-1.5" /> Close
            </Button>
          </div>
        </div>
      </DialogContent>
      {addNoteRow && (
        <NewDateDialog
          row={addNoteRow}
          onClose={() => setAddNoteRow(null)}
          onSaved={() => { setAddNoteRow(null); toast.success("New date recorded"); load(); }}
        />
      )}
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Doc ${formatBill(waRow.doc_no)} · ${waRow.service_name || ""}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="WhatsApp Reminder"
        />
      )}
    </Dialog>
  );
}

function NewDateDialog({ row, onClose, onSaved }) {
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!date) return toast.error("Please pick a follow-up date");
    if (!note.trim()) return toast.error("Please enter what the customer said");
    setSaving(true);
    try {
      await api.post("/follow-ups/upsert", { receipt_id: row.receipt_id, follow_up_date: date, note });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="overdue-newdate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Plus className="h-5 w-5 text-[color:var(--brand-teal)]" />
            New Promised Date — {row.customer_name}
          </DialogTitle>
          <DialogDescription>
            <b>Doc:</b> {formatBill(row.doc_no)} • <b>Balance:</b> <span className="text-red-600 font-bold">{inr(row.balance)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">New Payment Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="overdue-newdate-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">What did the customer say?</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder='e.g. "Will pay by 15th, salary credited on 10th…"'
              className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-navy)]"
              data-testid="overdue-newdate-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline"><X className="h-4 w-4 mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white font-semibold" data-testid="overdue-newdate-save">
            <CheckCircle2 className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
